import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import SEO from '../../components/SEO/SEO';
import FloatingImages from '../../components/FloatingImages/FloatingImages';
import Reveal, { RevealChild, STAGGER } from '../../components/motion/Reveal';
import StoreBadges from '../../components/common/StoreBadges/StoreBadges';
import { storeLabels } from '../../lib/appLinks';
import useMediaQuery from '../../hooks/useMediaQuery';
import { EASE } from '../../lib/motion';
import { POSE_CATEGORIES, HOW_IT_WORKS, FAQ_ITEMS } from './data';
import FloatingParticle from './components/FloatingParticle';
import FilmTitleIntro from './components/FilmTitleIntro';
import SectionMasthead from './components/SectionMasthead';
import StepDial from './components/StepDial';
import WorkflowDeck from './components/WorkflowDeck';
import WorkflowStacked from './components/WorkflowStacked';
import FAQItem from './components/FAQItem';
import styles from './Home.module.css';

/* Module-scoped, so it survives in-app navigation but resets on a full
   page load: refreshing the homepage replays the intro, while leaving
   for another route and returning does not. Home route only (this
   component mounts solely at "/"). `?intro=1` forces a replay. */
let introPlayedThisLoad = false;

/* ═══════════════════════════════════════════
   Main Page Component
   ═══════════════════════════════════════════ */

const Home: React.FC = () => {
  const reduced = useReducedMotion();
  const deckCapable = useMediaQuery('(min-width: 1024px)');
  // Fade + drift the hero on scroll, driven by the ABSOLUTE window scroll
  // position (not the hero element's measured rect). Absolute scroll only
  // grows while scrolling down, so the opacity is monotonic — it can never
  // pop back in mid-scroll-down (the earlier ref-measured version jumped
  // when the layout re-measured); it fades back in only on the way up.
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 480], [1, 0]);
  const heroY = useTransform(scrollY, [0, 700], [0, 120]);

  // Start every fresh mount of the homepage at the top — a scrolled
  // refresh (browser restoration is off; see index.tsx) or a return
  // from another route should reveal the hero, not mid-page. 'instant'
  // bypasses the global `scroll-behavior: smooth`. An explicit in-page
  // anchor in the URL (e.g. /#faq) is honored instead.
  useLayoutEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, []);

  // Cinematic entrance: the homepage opens like a screening on the
  // first mount of each full page load. Refreshing "/" replays it;
  // navigating to another route and back does NOT (the module flag
  // survives in-app navigation but resets on reload). Skippable; never
  // mounted under reduced motion. `?intro=1` forces a replay. The
  // initializer only READS the flag (no side effects in render — it is
  // double-invoked under StrictMode); the effect below records it.
  const [introWanted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (new URLSearchParams(window.location.search).has('intro')) return true;
    return !introPlayedThisLoad;
  });
  const playIntro = introWanted && !reduced;
  const [introGone, setIntroGone] = useState(!playIntro);

  // Hero entrance — released by the intro's reveal, or a beat after mount.
  const [heroLoaded, setHeroLoaded] = useState(false);
  // When the film gate plays it hides the hero, so paint the hero at rest
  // straight away: the LCP text becomes a candidate at first paint (behind
  // the gate) rather than only after the ~2.3s reveal, and the gate opening
  // is the reveal. Without the gate, keep the staged entrance.
  const heroReady = heroLoaded || playIntro || reduced;
  useEffect(() => {
    introPlayedThisLoad = true;
    if (!playIntro) {
      const timer = setTimeout(() => setHeroLoaded(true), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [playIntro]);

  // FAQ state
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const toggleFAQ = useCallback((index: number) => {
    setOpenFAQ((prev) => (prev === index ? null : index));
  }, []);

  // Gentle parallax on the mosaic
  const mosaicRef = useRef<HTMLDivElement>(null);
  const mosaicScroll = useScroll({
    target: mosaicRef,
    offset: ['start end', 'end start'],
  });
  const mosaicParallax = useTransform(
    mosaicScroll.scrollYProgress,
    [0, 1],
    [40, -40]
  );

  /** After the iris wipe the entrance is already underway — tighten the
   *  stagger so the title meets the opening aperture, not a dead beat. */
  const entranceDelay = useCallback(
    (t: number) => (playIntro ? Math.max(0.05, t - 0.35) : t),
    [playIntro]
  );

  /** Hero line that rises out of an overflow-hidden mask on load. */
  const heroLine = (delay: number, text: string) => (
    <span className={styles.heroTitleMask} key={text}>
      <motion.span
        className={styles.heroTitleLine}
        initial={reduced || playIntro ? false : { y: '110%' }}
        animate={heroReady ? { y: 0 } : {}}
        transition={{ duration: 1.1, delay: entranceDelay(delay), ease: EASE }}
      >
        {text}
      </motion.span>
    </span>
  );

  return (
    <div className={styles.page}>
      <SEO
        title="Pose Library, Shoot Planning & Client Galleries"
        keywords="photography app, pose ideas, photography poses, portrait poses, wedding photography, fashion photography, editorial poses, maternity poses, couple poses, family poses, photo shoot poses, booking management, client galleries, photography business, photographer tools, posing guide, photography inspiration, offline photography app"
        canonicalUrl="https://musephoto.app"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ_ITEMS.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }}
      />

      {!introGone && playIntro && (
        <FilmTitleIntro
          onReveal={() => setHeroLoaded(true)}
          onExit={() => setIntroGone(true)}
        />
      )}

      {/* ═══════════════════════════════════════════
          HERO — the floating print wall
          ═══════════════════════════════════════════ */}
      <section className={styles.hero} aria-labelledby="hero-title">
        <FloatingImages startDelay={playIntro ? 2.3 : 1.2} />

        {/* The darkroom safelight halo */}
        <div className={styles.heroGlow} />
        <div className={styles.heroGlowSecondary} />

        {/* Dust motes drifting in the projector beam */}
        <div className={styles.heroParticles} aria-hidden="true">
          <FloatingParticle delay={0} size={4} x="15%" y="20%" duration={6} />
          <FloatingParticle delay={1.5} size={3} x="80%" y="30%" duration={7} />
          <FloatingParticle delay={2.2} size={3} x="25%" y="70%" duration={8} />
          <FloatingParticle delay={1} size={4} x="90%" y="50%" duration={6.5} />
          <FloatingParticle delay={3} size={3} x="45%" y="15%" duration={7.5} />
          <FloatingParticle delay={0.5} size={6} x="70%" y="80%" duration={9} />
          <FloatingParticle delay={2.6} size={2} x="60%" y="40%" duration={8.5} />
        </div>

        <div className={styles.heroGrain} />
        <div className={styles.filmVignette} />

        <motion.div
          className={styles.heroContent}
          style={reduced ? undefined : { opacity: heroOpacity, y: heroY }}
        >
          <motion.p
            className={styles.heroFolio}
            initial={reduced || playIntro ? false : { opacity: 0 }}
            animate={heroReady ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: entranceDelay(0.35), ease: EASE }}
          >
            Poses · Bookings · Client galleries
          </motion.p>

          <h1 id="hero-title" className={styles.heroTitle}>
            {heroLine(0.55, 'Find the pose.')}
            {heroLine(0.7, 'Plan the shoot.')}
            {heroLine(0.85, 'Deliver the gallery.')}
          </h1>

          <motion.p
            className={styles.heroSubtitle}
            initial={reduced || playIntro ? false : { opacity: 0, y: 24 }}
            animate={heroReady ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: entranceDelay(1.1), ease: EASE }}
          >
            One app for the whole job: a pose library that works offline on
            set, bookings with shot lists, and client galleries that open like
            a private screening.
          </motion.p>

          <motion.div
            className={styles.heroActions}
            initial={reduced || playIntro ? false : { opacity: 0, y: 24 }}
            animate={heroReady ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: entranceDelay(1.3), ease: EASE }}
          >
            <StoreBadges />
          </motion.div>

          <motion.p
            className={styles.heroSmallPrint}
            initial={reduced || playIntro ? false : { opacity: 0 }}
            animate={heroReady ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: entranceDelay(1.55), ease: EASE }}
          >
            {storeLabels().note}
          </motion.p>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          01 · POSE LIBRARY — full-bleed photo mosaic
          ═══════════════════════════════════════════ */}
      <section
        id="library"
        ref={mosaicRef}
        className={styles.librarySection}
        aria-labelledby="library-title"
      >
        <div className={styles.librarySpread}>
          <SectionMasthead
            index="01"
            label="The pose library"
            titleId="library-title"
            title={
              <>
                The next pose is already
                <br />
                in your pocket.
              </>
            }
            subtitle="Scroll a discovery feed that learns what you shoot, or search by tag — session type, mood, light — then save what you like into collections. Mark the ones you’ll need on set as Save for Offline, and they open with no signal at all."
          />

          <Reveal className={styles.libraryVisual}>
            <figure className={styles.captionedFigure}>
              <div className={styles.phone}>
                <div className={styles.phoneScreen}>
                  <img
                    src="/screenshots/discover.webp"
                    alt="The Muse discovery feed — a personalised, scrollable stream of pose ideas from other photographers"
                    loading="lazy"
                    decoding="async"
                    width="640"
                    height="1392"
                    draggable={false}
                  />
                </div>
              </div>
              <figcaption className={styles.frameCaption}>
                Your discovery feed
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <motion.div
          className={styles.mosaic}
          style={reduced ? undefined : { y: mosaicParallax }}
        >
          {POSE_CATEGORIES.map((cat, i) => (
            <Reveal
              key={cat.name}
              className={`${styles.categoryTile} ${styles[cat.area]}`}
              delay={i * 0.06}
            >
              <img
                src={cat.image}
                srcSet={`${cat.image.replace('.webp', '-480.webp')} 480w, ${cat.image.replace('.webp', '-960.webp')} 960w`}
                sizes="(max-width: 1023px) 50vw, 25vw"
                alt={`${cat.name} poses from the Muse library`}
                className={styles.categoryImg}
                loading="lazy"
                decoding="async"
                width="300"
                height="200"
              />
              <div className={styles.categoryScrim} aria-hidden="true" />
              <div className={styles.categoryCaption}>
                <span className={styles.categoryName}>{cat.name}</span>
              </div>
            </Reveal>
          ))}
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          02 · THE WORKFLOW — pinned deck / stacked ledger
          ═══════════════════════════════════════════ */}
      {deckCapable && !reduced ? <WorkflowDeck /> : <WorkflowStacked />}

      {/* ═══════════════════════════════════════════
          03 · CLIENT GALLERIES — full-bleed premiere
          ═══════════════════════════════════════════ */}
      <section
        id="galleries"
        className={styles.galleriesSection}
        aria-labelledby="galleries-title"
      >
        <img
          src="/categories/family-1200.webp"
          srcSet="/categories/family-1200.webp 1200w, /categories/family.webp 1600w"
          sizes="100vw"
          alt=""
          className={styles.galleriesImage}
          loading="lazy"
          decoding="async"
          aria-hidden="true"
        />
        <div className={styles.galleriesScrim} aria-hidden="true" />
        <p className={styles.galleriesFolio} aria-hidden="true">
          Photographed by you
        </p>
        <Reveal
          staggerInterval={STAGGER.normal}
          className={styles.galleriesContent}
        >
          <RevealChild as="p" className={styles.mastheadEyebrow}>
            <span className={styles.mastheadIndex}>03</span>
            <span aria-hidden="true"> · </span>
            Client galleries
          </RevealChild>
          <RevealChild as="h2" className={styles.galleriesTitle} id="galleries-title">
            Delivery night feels
            <br />
            like a premiere.
          </RevealChild>
          <RevealChild as="p" className={styles.galleriesBody}>
            Every gallery opens with a film-title sequence and lays the photos
            out like prints on a light table. Clients can favourite the shots
            they love, comment on the gallery, and download the set — at full
            quality when you allow it — and you can add a PIN when a gallery
            should stay private.
          </RevealChild>
          <RevealChild className={styles.galleriesActions}>
            <Link to="/gallery/demo" className={styles.btnSecondary}>
              Open the demo gallery
            </Link>
          </RevealChild>
        </Reveal>
      </section>

      {/* ═══════════════════════════════════════════
          04 · COMMUNITY — challenges + daily spark
          ═══════════════════════════════════════════ */}
      <section
        id="community"
        className={styles.communitySection}
        aria-labelledby="community-title"
      >
        <div className={styles.communitySpread}>
          <div className={styles.communityCopy}>
            <SectionMasthead
              index="04"
              label="The community"
              titleId="community-title"
              title={
                <>
                  The best photo wins,
                  <br />
                  not the biggest account.
                </>
              }
              subtitle="A new theme every challenge, and every like counts as a vote. Small accounts win all the time here — the leaderboard doesn’t know your follower count."
            />
            <Reveal className={styles.sparkCard}>
              <p className={styles.sparkEyebrow}>Daily spark</p>
              <p className={styles.sparkPrompt}>
                &ldquo;Golden hour, hands in motion.&rdquo;
              </p>
              <p className={styles.sparkBody}>
                One line every morning, for the days you want to shoot but
                don&rsquo;t know what.
              </p>
            </Reveal>
          </div>

          <Reveal className={styles.communityVisual}>
            <figure className={styles.captionedFigure}>
              <div className={styles.phone}>
                <div className={styles.phoneScreen}>
                  <img
                    src="/screenshots/challenges.webp"
                    alt="Muse challenges screen showing a themed community competition"
                    loading="lazy"
                    decoding="async"
                    width="640"
                    height="1392"
                    draggable={false}
                  />
                </div>
              </div>
              <figcaption className={styles.frameCaption}>
                The current challenge
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          05 · GETTING STARTED — three-step cards
          ═══════════════════════════════════════════ */}
      <section
        id="how-it-works"
        className={styles.howSection}
        aria-labelledby="how-title"
      >
        <div className={styles.sectionParticles} aria-hidden="true">
          <FloatingParticle delay={0} size={3} x="8%" y="15%" duration={7} />
          <FloatingParticle delay={2} size={4} x="92%" y="25%" duration={6} />
          <FloatingParticle delay={1} size={2} x="20%" y="75%" duration={8} />
          <FloatingParticle delay={3} size={3} x="78%" y="65%" duration={7.5} />
        </div>

        <SectionMasthead
          index="05"
          label="Getting started"
          titleId="how-title"
          title={
            <>
              Three steps and
              <br />
              you&apos;re shooting.
            </>
          }
        />

        <ol className={styles.howSteps}>
          {HOW_IT_WORKS.map((step, i) => (
            <li key={step.step} className={styles.howStep}>
              <StepDial step={step.step} sweep={step.sweep} index={i} />
              <Reveal
                staggerInterval={STAGGER.normal}
                delay={i * 0.12}
                className={styles.howStepText}
              >
                <RevealChild as="p" className={styles.howKicker}>
                  <span className={styles.howKickerIndex}>{step.kicker}</span>
                </RevealChild>
                <RevealChild as="h3" className={styles.howTitle}>
                  {step.title}
                </RevealChild>
                <RevealChild as="p" className={styles.howDescription}>
                  {step.description}
                </RevealChild>
                <RevealChild as="p" className={styles.howLedger}>
                  {step.ledger}
                </RevealChild>
              </Reveal>
            </li>
          ))}
        </ol>
      </section>

      {/* ═══════════════════════════════════════════
          06 · FAQ — ledger
          ═══════════════════════════════════════════ */}
      <section
        id="faq"
        className={styles.faqSection}
        aria-labelledby="faq-title"
      >
        <SectionMasthead
          index="06"
          label="Questions"
          titleId="faq-title"
          title="Questions we get."
        />

        <div className={styles.faqList}>
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem
              key={i}
              question={item.question}
              answer={item.answer}
              isOpen={openFAQ === i}
              onToggle={() => toggleFAQ(i)}
              index={i}
              guideAnchor={item.guideAnchor}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          CTA — full-bleed closing frame
          ═══════════════════════════════════════════ */}
      <section
        id="get-the-app"
        className={styles.ctaSection}
        aria-labelledby="cta-title"
      >
        <img
          src="/categories/category-final-1200.webp"
          srcSet="/categories/category-final-1200.webp 1200w, /categories/category-final.webp 2000w"
          sizes="100vw"
          alt=""
          className={styles.ctaImage}
          loading="lazy"
          decoding="async"
          aria-hidden="true"
        />
        <div className={styles.ctaScrim} aria-hidden="true" />
        <Reveal staggerInterval={STAGGER.normal} className={styles.ctaContent}>
          <RevealChild as="p" className={styles.ctaFolio}>
            {storeLabels().note}
          </RevealChild>
          <RevealChild as="h2" className={styles.ctaTitle} id="cta-title">
            Bring Muse on
            <br />
            the next one.
          </RevealChild>
          <RevealChild className={styles.ctaActions}>
            <StoreBadges />
          </RevealChild>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
